import type { Download } from "./client";
import type { Helpers } from "./endpoints-shared";
import type { In, OperationId, Out } from "./operations";
import type { PageRequest } from "./types";

/** Billing runs, the minimum and statement close. Needs the `billing` module in the license. */
export function accountingEndpoints(h: Helpers) {
  const { get, post, paged, http } = h;
  const b = "/billing";
  /** A PDF download. The operation id is checked against the schema, so a renamed route fails to compile. */
  const file = (_operation: OperationId, path: string, query?: Record<string, string>) =>
    http.request<Download>("GET", path, { responseType: "blob", ...(query ? { query } : {}) });

  return {
    jonasChartReview: (limit = 50, offset = 0) => get<Out<"accounting_review_jonas_chart_rows">>("/accounting/jonas/chart/review", { query: { limit, offset } }),
    // ---- recurring and minimum runs
    /** Newest first. Discarded previews are not listed. */
    listRuns: (page: PageRequest & { kind?: string; status?: string } = {}) =>
      get<Out<"billing_list_runs">>(`${b}/runs`, paged(page, { kind: page.kind, status: page.status })),
    getRun: (id: string, signal?: AbortSignal) => get<Out<"billing_read_run">>(`${b}/runs/${id}`, { signal }),
    /** Replaces an earlier preview of the same period. Needs `billing.runs`. */
    previewRecurringRun: (input: In<"billing_preview_recurring_run">) => post<Out<"billing_preview_recurring_run">>(`${b}/runs/recurring/preview`, input),
    previewMinimumRun: (input: In<"billing_preview_minimum_run">) => post<Out<"billing_preview_minimum_run">>(`${b}/runs/minimum/preview`, input),
    listRunItems: (id: string, page: PageRequest & { status?: string; householdId?: string } = {}) =>
      get<Out<"billing_list_run_items">>(`${b}/runs/${id}/items`, paged(page, { status: page.status, household_id: page.householdId })),
    /** 409 `preview_stale` when accounts changed since the preview. */
    commitRun: (id: string) => post<Out<"billing_commit_a_run">>(`${b}/runs/${id}/commit`),
    reverseRun: (id: string, input: In<"billing_reverse_a_run">) => post<Out<"billing_reverse_a_run">>(`${b}/runs/${id}/reverse`, input),
    discardRun: (id: string) => post<Out<"billing_discard_a_run">>(`${b}/runs/${id}/discard`),
    /** The latest finished period of each active minimum plan, grouped by the day it ended. */
    minimumPeriods: (asOf?: string, signal?: AbortSignal) =>
      get<Out<"billing_minimum_periods">>(`${b}/minimum-periods`, { query: { as_of: asOf }, signal }),

    // ---- statement close
    statementPeriod: (signal?: AbortSignal) => get<Out<"billing_statement_period">>(`${b}/statement-period`, { signal }),
    listStatementRuns: (page: PageRequest & { status?: string } = {}) =>
      get<Out<"billing_list_statement_runs">>(`${b}/statement-runs`, paged(page, { status: page.status })),
    getStatementRun: (id: string, signal?: AbortSignal) => get<Out<"billing_read_statement_run">>(`${b}/statement-runs/${id}`, { signal }),
    previewStatementRun: (input: In<"billing_preview_a_statement_run">) => post<Out<"billing_preview_a_statement_run">>(`${b}/statement-runs/preview`, input),
    listStatementRunItems: (id: string, page: PageRequest & { q?: string; delivery?: string; withInterest?: boolean } = {}) =>
      get<Out<"billing_list_statement_run_items">>(
        `${b}/statement-runs/${id}/items`,
        paged(page, { q: page.q, delivery: page.delivery, with_interest: page.withInterest }),
      ),
    finaliseStatementRun: (id: string) => post<Out<"billing_finalise_a_statement_run">>(`${b}/statement-runs/${id}/finalise`),
    discardStatementRun: (id: string) => post<Out<"billing_discard_a_statement_run">>(`${b}/statement-runs/${id}/discard`),
    /** `deliverBy` "email" or "print" includes statements delivered both ways; `delivery` is the exact preference. */
    listStatements: (page: PageRequest & { runId?: string; householdId?: string; delivery?: string; deliverBy?: string; q?: string } = {}) =>
      get<Out<"billing_list_statements">>(
        `${b}/statements`,
        paged(page, { run_id: page.runId, household_id: page.householdId, delivery: page.delivery, deliver_by: page.deliverBy, q: page.q }),
      ),
    getStatement: (id: string, signal?: AbortSignal) => get<Out<"billing_read_statement">>(`${b}/statements/${id}`, { signal }),
    /** The stored statement as a PDF. */
    downloadStatementPdf: (id: string) => file("billing_download_statement", `${b}/statements/${id}/pdf`),
    /** One PDF with a finalised run's statements, for printing. Needs `billing.close`. */
    downloadStatementRunPdf: (id: string, filter: { deliverBy?: string; delivery?: string } = {}) =>
      file("billing_download_statement_run", `${b}/statement-runs/${id}/pdf`, {
        ...(filter.deliverBy ? { deliver_by: filter.deliverBy } : {}),
        ...(filter.delivery ? { delivery: filter.delivery } : {}),
      }),
  };
}
